import { z } from 'zod'

import {
  ScheduleReportFrequencyType,
  ScheduledReportDateRangePreset,
  daysOfTheWeek,
  scheduledReportConfigMaxHour,
  scheduledReportConfigMaxMin,
  scheduledReportConfigValidationErrorMessages as M,
} from '../constants/scheduledReportConfig'
import { ReportType } from '../constants/report'
import { normalizeScheduledReportConfigReportType } from '../utils/scheduledReportConfig'
import { dbEntitySchema } from './system'

const scheduledReportConfigReportTypeSchema = z.preprocess(
  normalizeScheduledReportConfigReportType,
  z.nativeEnum(ReportType)
)

const dayOfWeekSchema = z
  .number()
  .int()
  .min(0)
  .max(6, { message: M.REPORT_DAYS_OF_WEEK_BOUNDS })

const reportDaysOfWeekSchema = z
  .array(dayOfWeekSchema)
  .refine(
    (days) => new Set(days).size === days.length,
    M.REPORT_DAYS_OF_WEEK_BOUNDS
  )

const coerceHour = z.coerce
  .number()
  .refine((n) => Number.isInteger(n), { message: M.REPORT_TIME_HOUR_INTEGER })
  .refine((n) => n >= 0, { message: M.REPORT_TIME_HOUR_LT_MIN })
  .refine((n) => n <= scheduledReportConfigMaxHour, {
    message: M.REPORT_TIME_HOUR_GT_MAX,
  })

const coerceMinute = z.coerce
  .number()
  .refine((n) => Number.isInteger(n), { message: M.REPORT_TIME_MINUTE_INTEGER })
  .refine((n) => n >= 0, { message: M.REPORT_TIME_MINUTE_LT_MIN })
  .refine((n) => n <= scheduledReportConfigMaxMin, {
    message: M.REPORT_TIME_MINUTE_GT_MAX,
  })

const updateHour = z
  .number()
  .int({ message: M.REPORT_TIME_HOUR_INTEGER })
  .min(0, M.REPORT_TIME_HOUR_LT_MIN)
  .max(scheduledReportConfigMaxHour, M.REPORT_TIME_HOUR_GT_MAX)

const updateMinute = z
  .number()
  .int({ message: M.REPORT_TIME_MINUTE_INTEGER })
  .min(0, M.REPORT_TIME_MINUTE_LT_MIN)
  .max(scheduledReportConfigMaxMin, M.REPORT_TIME_MINUTE_GT_MAX)

const reportTimeNextSchema = z.preprocess((value) => {
  if (typeof value === 'number') return new Date(value)
  return value
}, z.coerce.date().optional().nullable())

/** Full document shape (Mongo + API serialization). */
export const scheduledReportConfigSchema = dbEntitySchema.extend({
  created: z.coerce.date(),
  modified: z.coerce.date(),
  createdById: z.string().optional(),
  organization: z.string().optional(),
  venue: z.string().optional(),
  // Legacy template generator compatibility fields.
  date: z.date().optional().nullable(),
  events: z.array(z.string()).optional().nullable(),
  includeReservations: z.boolean().optional(),
  includeAdmisions: z.boolean().optional(),
  name: z.string(),
  emails: z.array(z.string()).optional().nullable(),
  reportOptions: z.record(z.string(), z.any()).optional(),
  dateRangePreset: z.nativeEnum(ScheduledReportDateRangePreset).optional(),
  frequencyType: z.nativeEnum(ScheduleReportFrequencyType),
  reportType: scheduledReportConfigReportTypeSchema,
  reportDaysOfWeek: z.array(z.number()).optional().nullable(),
  reportTimeHour: z.number().int(),
  reportTimeMinute: z.number().int(),
  reportTimezone: z.string(),
  reportTimeNext: reportTimeNextSchema,
})

const scheduledReportConfigCreateBaseArgsSchema = z.object({
  name: z.string().min(1, M.NAME_REQUIRED),
  reportTimeHour: coerceHour,
  reportTimeMinute: coerceMinute,
  reportDaysOfWeek: reportDaysOfWeekSchema.optional(),
  reportType: scheduledReportConfigReportTypeSchema.default(
    ReportType.TLPRO_RESERVATION_DUMP
  ),
  reportOptions: z.record(z.string(), z.any()).optional(),
  dateRangePreset: z.nativeEnum(ScheduledReportDateRangePreset).optional(),
  recipientEmails: z.array(z.string().email()).optional(),
})

export const scheduledReportConfigVenueCreateArgsSchema =
  scheduledReportConfigCreateBaseArgsSchema.extend({
    venueId: z.string().min(1),
  })

export const scheduledReportConfigAdminCreateArgsSchema =
  scheduledReportConfigCreateBaseArgsSchema

// Legacy alias for existing callers.
export const scheduledReportConfigCreateArgsSchema =
  scheduledReportConfigVenueCreateArgsSchema

export const scheduledReportConfigUpdateByIdArgsSchema = z
  .object({
    name: z.string().min(1).optional(),
    reportTimeHour: updateHour.optional(),
    reportTimeMinute: updateMinute.optional(),
    reportDaysOfWeek: reportDaysOfWeekSchema.optional().nullable(),
    reportType: scheduledReportConfigReportTypeSchema.optional(),
    reportOptions: z.record(z.string(), z.any()).optional(),
    dateRangePreset: z
      .union([z.nativeEnum(ScheduledReportDateRangePreset), z.null()])
      .optional(),
  })
  .strict()
  .refine((data) => Object.keys(data).length > 0, {
    message: M.NO_UPDATABLE_FIELDS,
  })

export const scheduledReportConfigListQuerySchema = z.object({
  venueId: z.string().optional(),
  organizationId: z.string().optional(),
  createdById: z.string().optional(),
  platformOnly: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .optional(),
  limit: z.coerce.number().int().min(0).optional(),
  skip: z.coerce.number().int().min(0).optional(),
  sort: z.string().optional(),
})

export const scheduledReportConfigRecipientEmailsSchema = z
  .array(z.string().email())
  .min(1, M.EMAILS_REQUIRED)

/** Remove-recipient: strings only (legacy did not require RFC email format). */
export const scheduledReportConfigRecipientEmailStringsSchema = z
  .array(z.string().min(1, M.EMAILS_STRINGS))
  .min(1, M.EMAILS_REQUIRED)

/**
 * Validates merged config before insert or after update merge (replaces legacy
 * validator baseValidate).
 */
export const scheduledReportConfigActiveDocSchema = z
  .object({
    name: z.string().min(1, M.NAME_REQUIRED),
    createdById: z.string().optional(),
    venue: z.string().min(1, M.VENUE_REQUIRED).optional(),
    organization: z.string().min(1, M.ORGANIZATION_REQUIRED).optional(),
    reportType: scheduledReportConfigReportTypeSchema,
    frequencyType: z.nativeEnum(ScheduleReportFrequencyType),
    reportTimeHour: updateHour,
    reportTimeMinute: updateMinute,
    reportTimezone: z.string().min(1),
    reportDaysOfWeek: reportDaysOfWeekSchema.optional().nullable(),
    reportTimeNext: z.date({
      invalid_type_error: M.REPORT_TIME_NEXT_INVALID,
      required_error: M.REPORT_TIME_NEXT_INVALID,
    }),
    reportOptions: z.record(z.string(), z.any()).optional(),
    dateRangePreset: z.nativeEnum(ScheduledReportDateRangePreset).optional(),
    emails: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    const hasVenueScope = Boolean(data.venue || data.organization)
    if (hasVenueScope) {
      if (!data.venue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: M.VENUE_REQUIRED,
          path: ['venue'],
        })
      }
      if (!data.organization) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: M.ORGANIZATION_REQUIRED,
          path: ['organization'],
        })
      }
    } else if (!data.createdById) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'createdById is required for platform scheduled reports',
        path: ['createdById'],
      })
    }

    if (Number.isNaN(data.reportTimeNext.getTime())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: M.REPORT_TIME_NEXT_INVALID,
        path: ['reportTimeNext'],
      })
    }
    if (data.reportDaysOfWeek?.length) {
      const allowed = new Set(daysOfTheWeek)
      if (
        data.reportDaysOfWeek.some(
          (d) => !allowed.has(d as (typeof daysOfTheWeek)[number])
        )
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: M.REPORT_DAYS_OF_WEEK_BOUNDS,
          path: ['reportDaysOfWeek'],
        })
      }
      if (
        new Set(data.reportDaysOfWeek).size !== data.reportDaysOfWeek.length
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: M.REPORT_DAYS_OF_WEEK_BOUNDS,
          path: ['reportDaysOfWeek'],
        })
      }
    }
  })
